import img from "../assests/images/header-logo.png";
import img2 from "../assests/images/South-Travels-Footer-Logo.png";

import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import {
  ChevronDown,
  Heart,
  LogOut,
  Menu,
  User,
  X,
} from "lucide-react";

export default function Header({
  onLoginClick,
  user,
  setUser,
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileUserOpen, setMobileUserOpen] = useState(false);

  const dropdownRef = useRef(null);
  const mobileUserRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  // -----------------------------------------------------
  // SCROLL DETECTION
  // -----------------------------------------------------
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // -----------------------------------------------------
  // CLOSE DROPDOWNS WHEN CLICKING OUTSIDE
  // -----------------------------------------------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }

      if (
        mobileUserRef.current &&
        !mobileUserRef.current.contains(event.target)
      ) {
        setMobileUserOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  // -----------------------------------------------------
  // CLOSE MOBILE MENU WHEN ROUTE CHANGES
  // -----------------------------------------------------
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileUserOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // -----------------------------------------------------
  // PREVENT BODY SCROLL WHEN MOBILE MENU IS OPEN
  // -----------------------------------------------------
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // -----------------------------------------------------
  // HELPERS
  // -----------------------------------------------------
  const navigateTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileUserOpen(false);
  };

  const handleHomeClick = () => {
    closeMobileMenu();
    navigateTop();
  };

  const logout = () => {
    localStorage.removeItem("user");

    if (setUser) {
      setUser(null);
    }

    setDropdownOpen(false);
    setMobileUserOpen(false);
    setMobileMenuOpen(false);

    navigate("/");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };



  // -----------------------------------------------------
  // DESKTOP NAVIGATION STYLE
  // -----------------------------------------------------
  const desktopNavClass = ({ isActive }) => `
  relative
  inline-flex
  items-center
  px-1
  py-2
  text-[13px]
  xl:text-sm
  font-medium
  whitespace-nowrap
  transition-colors
  duration-200

  after:absolute
  after:left-1/2
  after:-translate-x-1/2
  after:-bottom-1
  after:h-[2px]
  after:w-0
  after:rounded-full
  after:bg-[#FFD43B]
  after:transition-all
  after:duration-200

  hover:after:w-full

  ${isActive
      ? "text-[#FFD43B] after:w-full"
      : isScrolled
        ? "text-[#0B1D3A] hover:text-[#174EA6]"
        : "text-white hover:text-[#FFD43B]"
    }
`;

  // -----------------------------------------------------
  // MOBILE NAVIGATION STYLE
  // -----------------------------------------------------
  const mobileNavClass = ({ isActive }) => `
    flex
    items-center
    justify-between
    w-full
    rounded-xl
    px-4
    py-3.5
    text-sm
    font-semibold
    transition-all
    duration-200

    ${isActive
      ? "bg-[#FFF7D6] text-[#174EA6]"
      : "text-[#1E293B] hover:bg-[#F8FAFC] hover:text-[#174EA6]"
    }
  `;

  return (
    <>
      {/* =====================================================
          MAIN HEADER
      ===================================================== */}

      <header
        className={`
          fixed
          top-0
          left-0
          z-[1000]
          w-full

          transition-all
          duration-300

          ${isScrolled
            ? "bg-white/95 shadow-[0_4px_20px_rgba(15,23,42,0.10)] backdrop-blur-md"
            : "bg-transparent"
          }
        `}
      >
        <div
          className="
            mx-auto
            w-full
            max-w-[1440px]
            px-5
            sm:px-8
            lg:px-10
            xl:px-12
          "
        >
          <div
            className={`
              flex
              items-center
              justify-between
              gap-5
              transition-all
              duration-300

              ${isScrolled
                ? "h-[68px]"
                : "h-[78px] lg:h-[86px]"
              }
            `}
          >
            {/* =================================================
                LOGO
            ================================================= */}

            <NavLink
              to="/"
              onClick={handleHomeClick}
              className="
                relative
                z-10
                flex
                shrink-0
                items-center
              "
              aria-label="Dubai Hotel Rates home"
            >
              {/* Logo for transparent hero header */}

              <img
                src={img2}
                alt="Dubai Hotel Rates"
                className={`
                  h-auto
                  w-[132px]
                  sm:w-[145px]
                  lg:w-[154px]
                  object-contain
                  transition-all
                  duration-300

                  ${isScrolled
                    ? "pointer-events-none absolute opacity-0 scale-95"
                    : "opacity-100 scale-100"
                  }
                `}
              />

              {/* Logo for white scrolled header */}

              <img
                src={img}
                alt="Dubai Hotel Rates"
                className={`
                  h-auto
                  w-[132px]
                  sm:w-[145px]
                  lg:w-[154px]
                  object-contain
                  transition-all
                  duration-300

                  ${isScrolled
                    ? "opacity-100 scale-100"
                    : "pointer-events-none absolute opacity-0 scale-95"
                  }
                `}
              />
            </NavLink>

            {/* =================================================
                DESKTOP NAVIGATION
            ================================================= */}

            <nav
              className="
    hidden
    lg:flex
    flex-1
    items-center
    justify-center
    gap-5
    xl:gap-7
    2xl:gap-8
  "
              aria-label="Main navigation"
            >
              <NavLink
                to="/"
                onClick={navigateTop}
                className={desktopNavClass}
              >
                Home
              </NavLink>

              <NavLink
                to="/hotels"
                className={desktopNavClass}
              >
                Hotels
              </NavLink>

              <NavLink
                to="/destinations"
                className={desktopNavClass}
              >
                Destinations
              </NavLink>

              <NavLink
                to="/blogs"
                className={desktopNavClass}
              >
                Blog
              </NavLink>

              <NavLink
                to="/about-us"
                className={desktopNavClass}
              >
                About Us
              </NavLink>

              <NavLink
                to="/contact-us"
                className={desktopNavClass}
              >
                Contact
              </NavLink>
            </nav>

            {/* =================================================
                DESKTOP RIGHT SIDE
            ================================================= */}

            <div
              className="
                hidden
                lg:flex
                items-center
                gap-4
                xl:gap-5
              "
            >


              {/* =================================================
                  WISHLIST
              ================================================= */}

              <NavLink
                to="/wishlist"
                aria-label="Wishlist"
                className={`
                  flex
                  items-center
                  gap-1.5
                  text-[13px]
                  font-medium
                  transition-colors
                  duration-200

                  ${isScrolled
                    ? "text-[#0F172A] hover:text-[#174EA6]"
                    : "text-white hover:text-[#FFD43B]"
                  }
                `}
              >
                <Heart
                  size={18}
                  strokeWidth={1.8}
                />

                <span>0</span>
              </NavLink>

              {/* =================================================
                  LOGIN / USER
              ================================================= */}

              {!user ? (
                <button
                  type="button"
                  onClick={onLoginClick}
                  className="
                    min-w-[116px]
                    rounded-md
                    bg-[#FFC928]
                    px-5
                    py-2.5
                    text-[12px]
                    font-bold
                    text-[#13294B]

                    shadow-[0_5px_15px_rgba(255,201,40,0.25)]

                    transition-all
                    duration-200

                    hover:-translate-y-0.5
                    hover:bg-[#FFD43B]
                    hover:shadow-lg

                    active:translate-y-0
                  "
                >
                  Sign In / Register
                </button>
              ) : (
                <div
                  ref={dropdownRef}
                  className="relative"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setDropdownOpen(
                        (prev) => !prev
                      );

                    }}
                    className={`
                      flex
                      items-center
                      gap-2
                      rounded-full
                      px-2
                      py-1.5
                      transition

                      ${isScrolled
                        ? "hover:bg-slate-100"
                        : "hover:bg-white/10"
                      }
                    `}
                  >
                    <span
                      className="
                        flex
                        h-8
                        w-8
                        items-center
                        justify-center
                        rounded-full
                        bg-[#174EA6]
                        text-sm
                        font-bold
                        text-white
                      "
                    >
                      {user?.name
                        ?.charAt(0)
                        ?.toUpperCase() || "U"}
                    </span>

                    <span
                      className={`
                        max-w-24
                        truncate
                        text-sm
                        font-semibold

                        ${isScrolled
                          ? "text-slate-800"
                          : "text-white"
                        }
                      `}
                    >
                      {user?.name?.split(
                        " "
                      )[0] || "Account"}
                    </span>

                    <ChevronDown
                      size={14}
                      className={`
                        transition-transform
                        duration-200

                        ${isScrolled
                          ? "text-slate-600"
                          : "text-white"
                        }

                        ${dropdownOpen
                          ? "rotate-180"
                          : ""
                        }
                      `}
                    />
                  </button>

                  {dropdownOpen && (
                    <div
                      className="
                        absolute
                        right-0
                        top-full
                        mt-2
                        w-52
                        overflow-hidden
                        rounded-2xl
                        border
                        border-slate-100
                        bg-white
                        py-1
                        shadow-2xl
                      "
                    >
                      <NavLink
                        to="/profile"
                        onClick={() =>
                          setDropdownOpen(false)
                        }
                        className="
                          flex
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-sm
                          text-slate-700
                          transition
                          hover:bg-slate-50
                          hover:text-[#174EA6]
                        "
                      >
                        <User size={17} />

                        My Profile
                      </NavLink>

                      <NavLink
                        to="/wishlist"
                        onClick={() =>
                          setDropdownOpen(false)
                        }
                        className="
                          flex
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-sm
                          text-slate-700
                          transition
                          hover:bg-slate-50
                          hover:text-[#174EA6]
                        "
                      >
                        <Heart size={17} />

                        Wishlist
                      </NavLink>

                      <button
                        type="button"
                        onClick={logout}
                        className="
                          flex
                          w-full
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-sm
                          text-red-500
                          transition
                          hover:bg-red-50
                        "
                      >
                        <LogOut size={17} />

                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* =================================================
                MOBILE MENU BUTTON
            ================================================= */}

            <button
              type="button"
              onClick={() =>
                setMobileMenuOpen(true)
              }
              aria-label="Open navigation menu"
              className={`
                flex
                h-10
                w-10
                items-center
                justify-center
                rounded-lg
                border
                transition-all
                duration-200
                lg:hidden

                ${isScrolled
                  ? "border-slate-200 bg-white text-[#174EA6] hover:bg-slate-50"
                  : "border-white/40 bg-white/10 text-white hover:bg-white/20"
                }
              `}
            >
              <Menu size={23} />
            </button>
          </div>
        </div>
      </header>

      {/* =====================================================
          MOBILE OVERLAY
      ===================================================== */}

      <div
        onClick={closeMobileMenu}
        aria-hidden={!mobileMenuOpen}
        className={`
          fixed
          inset-0
          z-[1090]
          bg-slate-950/50
          backdrop-blur-sm
          transition-opacity
          duration-300
          lg:hidden

          ${mobileMenuOpen
            ? "visible opacity-100"
            : "pointer-events-none invisible opacity-0"
          }
        `}
      />

      {/* =====================================================
          MOBILE SIDEBAR
      ===================================================== */}

      <aside
        aria-label="Mobile navigation"
        className={`
          fixed
          left-0
          top-0
          z-[1100]
          h-dvh
          w-[88%]
          max-w-[380px]
          overflow-y-auto
          bg-white
          shadow-2xl
          transition-transform
          duration-300
          ease-out
          lg:hidden

          ${mobileMenuOpen
            ? "translate-x-0"
            : "-translate-x-full"
          }
        `}
      >
        {/* =================================================
            MOBILE HEADER
        ================================================= */}

        <div
          className="
            flex
            items-center
            justify-between
            bg-[#062D67]
            px-5
            py-5
          "
        >
          <NavLink
            to="/"
            onClick={handleHomeClick}
            className="flex items-center"
          >
            <img
              src={img2}
              alt="Dubai Hotel Rates"
              className="
                h-auto
                w-[145px]
                object-contain
              "
            />
          </NavLink>

          <button
            type="button"
            onClick={closeMobileMenu}
            aria-label="Close navigation menu"
            className="
              flex
              h-10
              w-10
              items-center
              justify-center
              rounded-full
              bg-white/10
              text-white
              transition
              hover:bg-white/20
            "
          >
            <X size={22} />
          </button>
        </div>

        {/* =================================================
            MOBILE USER
        ================================================= */}

        <div
          className="
            border-b
            border-slate-100
            px-5
            py-4
          "
        >
          {user ? (
            <div
              ref={mobileUserRef}
              className="relative"
            >
              <button
                type="button"
                onClick={() =>
                  setMobileUserOpen(
                    (prev) => !prev
                  )
                }
                className="
                  flex
                  w-full
                  items-center
                  gap-3
                  text-left
                "
              >
                <span
                  className="
                    flex
                    h-11
                    w-11
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-[#174EA6]
                    text-base
                    font-bold
                    text-white
                  "
                >
                  {user?.name
                    ?.charAt(0)
                    ?.toUpperCase() || "U"}
                </span>

                <span
                  className="
                    min-w-0
                    flex-1
                  "
                >
                  <span
                    className="
                      block
                      truncate
                      text-sm
                      font-bold
                      text-slate-800
                    "
                  >
                    {user?.name || "Account"}
                  </span>

                  <span
                    className="
                      block
                      text-xs
                      text-slate-500
                    "
                  >
                    Tap to manage account
                  </span>
                </span>

                <ChevronDown
                  size={18}
                  className={`
                    text-slate-500
                    transition-transform
                    duration-200

                    ${mobileUserOpen
                      ? "rotate-180"
                      : ""
                    }
                  `}
                />
              </button>

              {mobileUserOpen && (
                <div
                  className="
                    mt-3
                    overflow-hidden
                    rounded-xl
                    border
                    border-slate-100
                    bg-slate-50
                  "
                >
                  <NavLink
                    to="/profile"
                    onClick={closeMobileMenu}
                    className="
                      flex
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-sm
                      text-slate-700
                      hover:bg-white
                      hover:text-[#174EA6]
                    "
                  >
                    <User size={17} />

                    My Profile
                  </NavLink>

                  <NavLink
                    to="/wishlist"
                    onClick={closeMobileMenu}
                    className="
                      flex
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-sm
                      text-slate-700
                      hover:bg-white
                      hover:text-[#174EA6]
                    "
                  >
                    <Heart size={17} />

                    Wishlist
                  </NavLink>

                  <button
                    type="button"
                    onClick={logout}
                    className="
                      flex
                      w-full
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-sm
                      text-red-500
                      hover:bg-red-50
                    "
                  >
                    <LogOut size={17} />

                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                onLoginClick?.();
                closeMobileMenu();
              }}
              className="
                w-full
                rounded-lg
                bg-[#FFC928]
                px-4
                py-3.5
                text-sm
                font-bold
                text-[#13294B]
                shadow-md
                transition
                hover:bg-[#FFD43B]
              "
            >
              Sign In / Register
            </button>
          )}
        </div>

        {/* =================================================
            MOBILE NAVIGATION
        ================================================= */}

        <nav
          className="
            space-y-1
            px-4
            py-5
          "
        >
          <NavLink
            to="/"
            onClick={handleHomeClick}
            className={mobileNavClass}
          >
            Home
          </NavLink>

          <NavLink
            to="/hotels"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Hotels
          </NavLink>



          <NavLink
            to="/destinations"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Destinations
          </NavLink>

          <NavLink
            to="/blogs"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Blog
          </NavLink>

          <NavLink
            to="/about-us"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            About Us
          </NavLink>

          <NavLink
            to="/contact-us"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Contact
          </NavLink>

          {/* =================================================
              MOBILE WISHLIST
          ================================================= */}

          <NavLink
            to="/wishlist"
            onClick={closeMobileMenu}
            className="
              mt-4
              flex
              items-center
              justify-center
              gap-2
              rounded-lg
              border
              border-[#174EA6]/10
              bg-[#F5F8FF]
              px-4
              py-3.5
              text-sm
              font-bold
              text-[#174EA6]
              transition
              hover:bg-[#FFF7D6]
            "
          >
            <Heart size={18} />

            Wishlist

            <span
              className="
                rounded-full
                bg-[#174EA6]
                px-2
                py-0.5
                text-[11px]
                text-white
              "
            >
              0
            </span>
          </NavLink>


        </nav>
      </aside>
    </>
  );
}